import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/hooks/use-language';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Users, Calendar, ArrowLeft, Crown, Medal } from 'lucide-react';
import { toast } from 'sonner';

interface Tournament {
  id: string;
  name: string;
  tournament_type: string;
  total_rounds: number;
  completed_rounds: number;
  is_completed: boolean;
  created_at: string;
  completed_at?: string;
}

interface Match {
  id: string;
  round_number: number;
  court_number: number;
  team1_score: number;
  team2_score: number;
  winner_team: number;
  team1_player1: { name: string };
  team1_player2: { name: string };
  team2_player1: { name: string };
  team2_player2: { name: string };
}

interface PlayerStats {
  name: string;
  wins: number;
  losses: number;
  totalPoints: number;
  gamesPlayed: number;
}

export default function TournamentResults() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [playerStats, setPlayerStats] = useState<PlayerStats[]>([]);
  const [loading, setLoading] = useState(true);

  const calculatePlayerStats = useCallback((matchesData: Match[]) => {
    const stats = new Map<string, PlayerStats>();

    matchesData.forEach(match => {
      const team1Players = [match.team1_player1.name, match.team1_player2.name];
      const team2Players = [match.team2_player1.name, match.team2_player2.name];
      
      // Initialize player stats if not exists
      [...team1Players, ...team2Players].forEach(playerName => {
        if (!stats.has(playerName)) {
          stats.set(playerName, {
            name: playerName,
            wins: 0,
            losses: 0,
            totalPoints: 0,
            gamesPlayed: 0
          });
        }
      });

      const team1Won = match.winner_team === 1;

      // Update team 1 players
      team1Players.forEach(playerName => {
        const playerStat = stats.get(playerName)!;
        playerStat.gamesPlayed++;
        playerStat.totalPoints += match.team1_score;
        if (team1Won) {
          playerStat.wins++;
        } else {
          playerStat.losses++;
        }
      });

      // Update team 2 players
      team2Players.forEach(playerName => {
        const playerStat = stats.get(playerName)!;
        playerStat.gamesPlayed++;
        playerStat.totalPoints += match.team2_score;
        if (!team1Won) {
          playerStat.wins++;
        } else {
          playerStat.losses++;
        }
      });
    });

    // Sort by wins desc, then by total points desc
    const sortedStats = Array.from(stats.values()).sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      return b.totalPoints - a.totalPoints;
    });

    setPlayerStats(sortedStats);
  }, []);

  const fetchTournamentData = useCallback(async (tournamentId: string) => {
    try {
      setLoading(true);
      
      // Fetch tournament details
      const { data: tournamentData, error: tournamentError } = await supabase
        .from('tournaments')
        .select('*')
        .eq('id', tournamentId)
        .single();

      if (tournamentError) throw tournamentError;
      setTournament(tournamentData);

      // Fetch matches with player names
      const { data: matchesData, error: matchesError } = await supabase
        .from('matches')
        .select(`
          *,
          team1_player1:players!matches_team1_player1_id_fkey(name),
          team1_player2:players!matches_team1_player2_id_fkey(name),
          team2_player1:players!matches_team2_player1_id_fkey(name),
          team2_player2:players!matches_team2_player2_id_fkey(name)
        `)
        .eq('tournament_id', tournamentId)
        .order('round_number')
        .order('court_number');

      if (matchesError) throw matchesError;
      setMatches(matchesData || []);

      // Calculate player statistics
      calculatePlayerStats(matchesData || []);

    } catch (error) {
      console.error('Error fetching tournament data:', error);
      toast.error(t('tournament.error_loading_results'));
    } finally {
      setLoading(false);
    }
  }, [t, calculatePlayerStats]);

  const fetchLatestTournament = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data: tournamentData, error } = await supabase
        .from('tournaments')
        .select('*')
        .eq('is_completed', true)
        .order('completed_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !tournamentData) {
        toast.error(t('tournament.no_results_found'));
        navigate('/tournaments');
        return;
      }

      setTournament(tournamentData);
      fetchTournamentData(tournamentData.id);
    } catch (error) {
      console.error('Error fetching latest tournament:', error);
      toast.error(t('tournament.error_loading_results'));
      navigate('/tournaments');
    }
  }, [t, navigate, fetchTournamentData]);

  useEffect(() => {
    if (id) {
      fetchTournamentData(id);
    } else {
      // Fallback: fetch latest completed tournament
      fetchLatestTournament();
    }
  }, [id, fetchTournamentData, fetchLatestTournament]);

  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Crown className="h-5 w-5 text-yellow-500" />;
      case 2:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 3:
        return <Medal className="h-5 w-5 text-amber-600" />;
      default:
        return <span className="text-sm font-medium">#{position}</span>;
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="p-6 max-w-4xl mx-auto text-center">
        <h1 className="text-2xl font-bold mb-4">{t('tournament.not_found')}</h1>
        <Button onClick={() => navigate('/tournaments')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('tournament.back_to_tournaments')}
        </Button>
      </div>
    );
  }

  const roundGroups = matches.reduce((acc, match) => {
    if (!acc[match.round_number]) {
      acc[match.round_number] = [];
    }
    acc[match.round_number].push(match);
    return acc;
  }, {} as Record<number, Match[]>);

  const hasMatches = matches.length > 0;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/tournaments')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('tournament.back_to_tournaments')}
        </Button>
        <Badge variant={tournament.is_completed ? 'default' : 'secondary'}>
          {tournament.is_completed ? t('tournament.completed') : t('tournament.in_progress')}
        </Badge>
      </div>

      {/* Tournament Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-6 w-6 text-primary" />
            {tournament.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span>{tournament.tournament_type.replace('_', ' ').toUpperCase()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>{new Date(tournament.created_at).toLocaleDateString()}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('tournament.rounds')}: </span>
              <span>{tournament.completed_rounds}/{tournament.total_rounds}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('tournament.players')}: </span>
              <span>{playerStats.length}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Player Rankings */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tournament.final_standings')}</CardTitle>
        </CardHeader>
        <CardContent>
          {hasMatches ? (
            <div className="space-y-3">
              {playerStats.map((player, index) => (
                <div key={player.name} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {getPositionIcon(index + 1)}
                    <span className="font-medium">{player.name}</span>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <span>{t('tournament.wins')}: <span className="font-medium text-foreground">{player.wins}</span></span>
                    <span>{t('tournament.losses')}: <span className="font-medium text-foreground">{player.losses}</span></span>
                    <span>{t('tournament.points')}: <span className="font-medium text-foreground">{player.totalPoints}</span></span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">{t('tournament.no_results_found')}</div>
          )}
        </CardContent>
      </Card>

      {/* Round Results */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold">{t('tournament.round_results')}</h2>
        {hasMatches ? Object.entries(roundGroups).map(([roundNumber, roundMatches]) => (
          <Card key={roundNumber}>
            <CardHeader>
              <CardTitle className="text-lg">
                {t('tournament.round')} {roundNumber}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {roundMatches.map((match) => (
                  <div key={match.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <Badge variant="outline">{t('tournament.court')} {match.court_number}</Badge>
                      <div className="text-sm">
                        <div className="font-medium">
                          {match.team1_player1.name} & {match.team1_player2.name}
                        </div>
                        <div className="text-muted-foreground">vs</div>
                        <div className="font-medium">
                          {match.team2_player1.name} & {match.team2_player2.name}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold">
                        {match.team1_score} - {match.team2_score}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {match.winner_team === 1 
                          ? `${match.team1_player1.name} & ${match.team1_player2.name}` 
                          : `${match.team2_player1.name} & ${match.team2_player2.name}`
                        } {t('tournament.won')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )) : (
          <Card>
            <CardContent>
              <div className="text-sm text-muted-foreground py-6">{t('tournament.no_results_found')}</div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
